import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import config from '../Config';
import { authFetch } from '../functions/authFetch';
import { HandleResponse } from '../functions/HandleResponse';
import { useSharedClusterPreview } from '../components/SharedClusterGate';
import './pages.css';

export default function DomainManagement() {
  const navigate = useNavigate();
  const base = `${config.RUNNER_API}/v1/domain`;

  // Shared cluster: the screens are browsable, but no certificate can be requested.
  const isPreview = useSharedClusterPreview();

  // ── list ──────────────────────────────────────────────────────────────────
  const [domains, setDomains]       = useState([]);
  const [listLoading, setListLoading] = useState(true);
  const [listError, setListError]   = useState(null);

  // ── right-panel mode: null | 'add' | 'detail' ────────────────────────────
  const [panelMode, setPanelMode]   = useState(null);
  const [selected, setSelected]     = useState(null); // domain object

  // ── add domain form ───────────────────────────────────────────────────────
  const [newDomain, setNewDomain]   = useState('');
  const [addLoading, setAddLoading] = useState(false);
  const [addMsg, setAddMsg]         = useState(null);
  const [addErr, setAddErr]         = useState(null);

  // ── refresh / check status ────────────────────────────────────────────────
  const [checking, setChecking]     = useState(false);
  const [checkMsg, setCheckMsg]     = useState(null);

  // ── delete confirm ────────────────────────────────────────────────────────
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting]         = useState(false);
  const deleteModalRef = useRef(null);

  // ── fetch list (optionally run ACM check first) ────────────────────────────
  const fetchDomains = useCallback(async (runCheck = false) => {
    setListLoading(true);
    setListError(null);
    try {
      if (runCheck) {
        setChecking(true);
        setCheckMsg(null);
        const checkRes = await authFetch(`${base}/check`, { method: 'POST' });
        if (checkRes.ok) {
          const d = await checkRes.json();
          const nv = d.newly_verified || [];
          if (nv.length > 0) {
            setCheckMsg(`✓ ${nv.join(', ')} ${nv.length === 1 ? 'is' : 'are'} now verified!`);
          }
        }
        setChecking(false);
      }
      const res = await authFetch(`${base}/list`, { method: 'GET' });
      await HandleResponse(res, navigate);
      if (res.ok) {
        const data = await res.json();
        const list = Array.isArray(data) ? data : [];
        setDomains(list);
        // Keep the detail panel in sync if a domain is selected
        setSelected(prev => {
          if (!prev) return prev;
          return list.find(d => d.domain === prev.domain) || prev;
        });
      }
    } catch {
      setListError('Failed to load domains.');
    } finally {
      setListLoading(false);
      setChecking(false);
    }
  }, [base, navigate]);

  useEffect(() => { fetchDomains(true); }, [fetchDomains]);

  // Click-outside close for delete modal
  useEffect(() => {
    if (!deleteTarget) return;
    const handler = (e) => {
      if (deleteModalRef.current && !deleteModalRef.current.contains(e.target)) {
        setDeleteTarget(null);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [deleteTarget]);

  // ── open detail ───────────────────────────────────────────────────────────
  const openDomain = (d) => {
    setSelected(d);
    setPanelMode('detail');
    setAddMsg(null);
    setAddErr(null);
  };

  const openAdd = () => {
    setPanelMode('add');
    setSelected(null);
    setNewDomain('');
    setAddMsg(null);
    setAddErr(null);
  };

  // ── add domain ────────────────────────────────────────────────────────────
  const handleAdd = async () => {
    if (isPreview) return;
    const d = newDomain.trim().toLowerCase().replace(/^www\./, '').trim('.');
    if (!d) { setAddErr('Please enter a domain.'); return; }
    if (!d.includes('.')) { setAddErr('Enter a full domain like example.com'); return; }

    setAddLoading(true);
    setAddMsg(null);
    setAddErr(null);
    try {
      const res = await authFetch(`${base}/add`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ domain: d }),
      });
      await HandleResponse(res, navigate);
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        setAddMsg(`Certificate requested for *.${d}. Add the CNAME below to verify.`);
        await fetchDomains(false);
        // Switch to the new domain's detail panel
        setSelected(data);
        setPanelMode('detail');
        setNewDomain('');
      } else {
        setAddErr(data.detail || 'Failed to add domain.');
      }
    } catch { setAddErr('Failed to add domain.'); }
    finally { setAddLoading(false); }
  };

  // ── delete ────────────────────────────────────────────────────────────────
  const handleDelete = async () => {
    if (!deleteTarget || isPreview) return;
    setDeleting(true);
    try {
      const res = await authFetch(
        `${base}/delete?domain=${encodeURIComponent(deleteTarget)}`,
        { method: 'DELETE' },
      );
      await HandleResponse(res, navigate);
      if (res.ok) {
        setDomains(prev => prev.filter(d => d.domain !== deleteTarget));
        if (selected?.domain === deleteTarget) { setSelected(null); setPanelMode(null); }
      }
    } catch { }
    finally { setDeleting(false); setDeleteTarget(null); }
  };

  // ── CNAME table component (reused for both add result and detail) ──────────
  const CnameTable = ({ cname_name, cname_value }) => (
    <table className="dm-cname-table">
      <thead>
        <tr>
          <th>Type</th>
          <th>Name (Host)</th>
          <th>Value (Points To)</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td><code>CNAME</code></td>
          <td>
            <code className="dm-cname-val">{cname_name}</code>
            <button className="dm-copy-btn" onClick={() => navigator.clipboard.writeText(cname_name)} title="Copy">⧉</button>
          </td>
          <td>
            <code className="dm-cname-val">{cname_value}</code>
            <button className="dm-copy-btn" onClick={() => navigator.clipboard.writeText(cname_value)} title="Copy">⧉</button>
          </td>
        </tr>
      </tbody>
    </table>
  );

  // ── render instruction panel ─────────────────────────────────────────────
  const renderInstructions = () => (
    <div className="page-inst-panel">
      <h4>How to verify</h4>
      <ol>
        <li>Click <strong>+ Add Domain</strong> and enter your bare domain (e.g. <code>example.com</code>).</li>
        <li>Copy the <strong>CNAME Name</strong> and <strong>Value</strong> shown in the detail panel.</li>
        <li>Add a CNAME record in your DNS provider using the Name and Value shown in the detail panel.</li>
        <li>Click <strong>↻ Check Status</strong>. Verification usually takes 5–30 minutes.</li>
        <li>Once verified, go to <Link className="inst-link" to="/deployment/apps">Deployments</Link> and select this domain as the custom host for your app.</li>
      </ol>
      <hr className="inst-divider" />
      <p>The certificate covers both the wildcard (<code>*.yourdomain.com</code>) and the apex domain, so any subdomain prefix you set works automatically.</p>
      <hr className="inst-divider" />
      <p>Up to <strong>10 domains</strong> per account are allowed.</p>
    </div>
  );

  // ── render right panel ────────────────────────────────────────────────────
  const renderPanel = () => {
    // ── Add form ──
    if (panelMode === 'add') return (
      <div className="dm-form-panel">
        <div className="dm-panel-header">
          <h3 className="dm-panel-title">Register a New Domain</h3>
          <button className="cm-btn cm-btn-ghost cm-btn-sm" onClick={() => setPanelMode(null)}>✕</button>
        </div>
        <p className="dm-hint" style={{ marginBottom: '1rem' }}>
          Enter your bare domain (e.g. <code>example.com</code>). We will request an ACM wildcard
          certificate covering <code>*.example.com</code> and <code>example.com</code>.
          You will need to add a CNAME record to your DNS provider to verify ownership.
          Up to <strong>10 domains</strong> are allowed.
        </p>
        <div className="dm-add-row">
          <span className="dm-add-prefix">www.</span>
          <input
            className="cm-input dm-input dm-domain-input"
            placeholder="example.com"
            value={newDomain}
            onChange={e => setNewDomain(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleAdd()}
            disabled={addLoading}
          />
        </div>
        {addErr && <p className="cm-error" style={{ marginTop: '0.5rem' }}>{addErr}</p>}
        {addMsg && <p className="cm-success" style={{ marginTop: '0.5rem' }}>{addMsg}</p>}
        {isPreview && (
          <p className="dm-hint" style={{ marginTop: '0.5rem' }}>
            You are on the shared cluster, so no certificate can be requested here. Custom domains
            terminate TLS on your own load balancer, which comes with your own cluster.{' '}
            <Link className="inst-link" to="/cluster">Create one</Link> to add this domain.
          </p>
        )}
        <div style={{ marginTop: '1rem', display: 'flex', gap: '0.5rem' }}>
          <button
            className="am-btn-next"
            onClick={handleAdd}
            disabled={isPreview || addLoading || !newDomain.trim()}
            title={isPreview ? 'Needs a dedicated cluster' : undefined}
          >
            {addLoading ? 'Requesting…' : 'Request Certificate'}
          </button>
          <button className="dm-btn-secondary" onClick={() => setPanelMode(null)} disabled={addLoading}>
            Cancel
          </button>
        </div>
      </div>
    );

    // ── Domain detail ──
    if (panelMode === 'detail' && selected) {
      const d = selected;
      return (
        <div className="dm-form-panel">
          <div className="dm-panel-header">
            <div>
              <h3 className="dm-panel-title">{d.domain}</h3>
              <span className={`dm-badge ${d.verified ? 'dm-badge--ok' : 'dm-badge--pending'}`} style={{ marginTop: '0.25rem', display: 'inline-block' }}>
                {d.verified ? '● Verified' : '● Pending DNS validation'}
              </span>
            </div>
            <button
              className="dm-btn-delete"
              onClick={() => setDeleteTarget(d.domain)}
              disabled={isPreview}
              title={isPreview ? 'Needs a dedicated cluster' : undefined}
            >
              Delete
            </button>
          </div>

          <div className="dm-detail-grid" style={{ marginBottom: '1.25rem' }}>
            <span className="dm-detail-label">Certificate covers</span>
            <span className="dm-detail-val"><code>*.{d.domain}</code> and <code>{d.domain}</code></span>
            <span className="dm-detail-label">Added</span>
            <span className="dm-detail-val">
              {d.created_at ? new Date(d.created_at).toLocaleString() : '—'}
            </span>
          </div>

          <h4 className="dm-section-title">DNS Validation Record</h4>
          {d.cname_name ? (
            <>
              <CnameTable cname_name={d.cname_name} cname_value={d.cname_value} />
              {!d.verified && (
                <p className="dm-hint" style={{ marginTop: '0.6rem' }}>
                  Add this CNAME to your DNS provider, then click <strong>↻ Check Status</strong> in the sidebar.
                </p>
              )}
              {d.verified && (
                <p className="cm-success" style={{ marginTop: '0.75rem' }}>
                  ✓ Certificate is active. Select <strong>{d.domain}</strong> as the custom domain
                  in Deployments to use it.
                </p>
              )}
            </>
          ) : (
            <p className="dm-hint">
              CNAME records are still being generated by ACM — click <strong>↻ Check Status</strong> in a few seconds to retrieve them.
            </p>
          )}
        </div>
      );
    }

    // ── Empty state ──
    return (
      <div className="cm-empty-state">
        <p>Select a domain to view its details,<br />or click <strong>+ Add Domain</strong> to register one.</p>
        <p className="dm-hint" style={{ marginTop: '0.5rem' }}>
          Each domain gets a wildcard ACM certificate (<code>*.yourdomain.com</code>) so any
          subdomain you configure in Deployments will be covered by HTTPS.
        </p>
      </div>
    );
  };

  // ── main ──────────────────────────────────────────────────────────────────
  return (
    <div className="cm-page">
      <h2 className="cm-title">Domains</h2>
      <p className="cm-subtitle">Register custom domains and manage ACM TLS certificates for your deployments.</p>

      <div className="cm-layout">
        {/* Sidebar */}
        <div className="cm-sidebar">
          <div className="cm-sidebar-header">
            <span>Domains</span>
            <button className="cm-btn cm-btn-primary cm-btn-sm" onClick={openAdd}>+ Add Domain</button>
          </div>

          {listLoading && <p className="cm-hint" style={{ padding: '0.5rem 1rem' }}>Loading…</p>}
          {listError   && <p className="cm-error" style={{ padding: '0.5rem 1rem' }}>{listError}</p>}
          {!listLoading && domains.length === 0 && (
            <p className="cm-hint" style={{ padding: '0.5rem 1rem' }}>No domains yet.</p>
          )}

          <ul className="cm-list">
            {domains.map(d => (
              <li
                key={d.domain}
                className={`cm-list-item cm-list-item-stacked${selected?.domain === d.domain && panelMode === 'detail' ? ' active' : ''}`}
                onClick={() => openDomain(d)}
              >
                <div className="cm-list-item-main">
                  <span className="cm-list-name">{d.domain}</span>
                  <span
                    className={`dm-deploy-dot ${d.verified ? 'dm-dot-deployed' : 'dm-dot-pending'}`}
                    title={d.verified ? 'Verified' : 'Pending verification'}
                  />
                </div>
              </li>
            ))}
          </ul>

          {/* Sidebar footer: refresh button */}
          <div style={{ padding: '0.75rem 1rem', borderTop: '1px solid #e0e4ea', marginTop: 'auto' }}>
            <button
              className="dm-btn-secondary"
              style={{ width: '100%' }}
              onClick={() => fetchDomains(true)}
              disabled={isPreview || checking || listLoading}
              title={isPreview ? 'Needs a dedicated cluster' : undefined}
            >
              {checking ? 'Checking…' : '↻ Check Status'}
            </button>
            {checkMsg && (
              <p className="cm-success" style={{ marginTop: '0.4rem', fontSize: '0.8rem' }}>{checkMsg}</p>
            )}
          </div>
        </div>

        {/* Right panel */}
        <div className="cm-detail" style={{ minHeight: 480 }}>
          {renderPanel()}
        </div>

        {/* Guide panel */}
        {renderInstructions()}
      </div>

      {/* Delete confirmation modal */}
      {deleteTarget && (
        <div className="cm-modal-overlay">
          <div className="cm-modal" ref={deleteModalRef}>
            <p className="cm-modal-title">Remove Domain</p>
            <p className="cm-modal-body">
              Delete <strong>{deleteTarget}</strong>?<br />
              The domain record and its ACM certificate will be permanently removed.
              Make sure no load balancer is actively using this certificate before proceeding.
            </p>
            <div className="cm-modal-buttons">
              <button className="cm-btn cm-btn-ghost" onClick={() => setDeleteTarget(null)} disabled={deleting}>
                Cancel
              </button>
              <button className="dm-btn-delete" onClick={handleDelete} disabled={deleting}>
                {deleting ? 'Removing…' : 'Remove'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
